/** IndexedDB media library — shares imported blobs across dual-window tabs. */

const DB_NAME = 'cinegrid-media-v1';
const STORE = 'blobs';
const PROJECT_STORE = 'project';
const DB_VERSION = 2;

export type StoredMedia = {
  id: string;
  blob: Blob;
  mimeType: string;
  name: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PROJECT_STORE)) {
        db.createObjectStore(PROJECT_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

export async function putMediaBlob(
  id: string,
  blob: Blob,
  mimeType: string,
  name: string
): Promise<void> {
  // Copy into a plain Blob so IndexedDB persistence is reliable across browsers/tabs
  const buffer = await blob.arrayBuffer();
  const durable = new Blob([buffer], { type: mimeType || blob.type || 'application/octet-stream' });
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({
      id,
      blob: durable,
      mimeType: mimeType || durable.type,
      name,
    } satisfies StoredMedia);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB put failed'));
  });
  db.close();
}

export async function getMediaBlob(id: string): Promise<StoredMedia | null> {
  const db = await openDb();
  const row = await new Promise<StoredMedia | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => resolve((req.result as StoredMedia | undefined) ?? null);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB get failed'));
  });
  db.close();
  return row;
}

export async function deleteMediaBlob(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB delete failed'));
  });
  db.close();
}

/** Per-tab object URLs created from IndexedDB (never another tab's blob: URL). */
const urlCache = new Map<string, string>();

type MediaFetcher = (assetId: string) => Promise<{
  buffer: ArrayBuffer;
  mimeType: string;
  name: string;
} | null>;

let remoteFetcher: MediaFetcher | null = null;

/** Tab sync registers this so followers can pull media from the leader. */
export function setRemoteMediaFetcher(fn: MediaFetcher | null) {
  remoteFetcher = fn;
}

async function blobUrlWorks(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    if (res.ok) return true;
  } catch {
    /* fall through */
  }
  try {
    const res = await fetch(url);
    return res.ok && (await res.blob()).size > 0;
  } catch {
    return false;
  }
}

/**
 * Resolve a playable URL for an asset in this tab.
 * Dual-window followers can't use another tab's blob: URLs — reload from IndexedDB
 * or request bytes from the leader tab.
 */
export async function resolveAssetUrl(asset: {
  id: string;
  url: string;
  name?: string;
  mimeType?: string;
}): Promise<string> {
  const cached = urlCache.get(asset.id);
  if (cached) return cached;

  const stored = await getMediaBlob(asset.id);
  if (stored?.blob && stored.blob.size > 0) {
    const url = URL.createObjectURL(stored.blob);
    urlCache.set(asset.id, url);
    return url;
  }

  // Same-tab blob URL still valid?
  if (asset.url?.startsWith('blob:') && (await blobUrlWorks(asset.url))) {
    // Persist for other tabs
    try {
      const res = await fetch(asset.url);
      const blob = await res.blob();
      await putMediaBlob(asset.id, blob, asset.mimeType || blob.type, asset.name || asset.id);
    } catch {
      /* ignore persist errors */
    }
    urlCache.set(asset.id, asset.url);
    return asset.url;
  }

  // Ask leader tab for bytes
  if (remoteFetcher) {
    const remote = await remoteFetcher(asset.id);
    if (remote && remote.buffer.byteLength > 0) {
      const blob = new Blob([remote.buffer], {
        type: remote.mimeType || 'application/octet-stream',
      });
      await putMediaBlob(asset.id, blob, remote.mimeType, remote.name);
      const url = URL.createObjectURL(blob);
      urlCache.set(asset.id, url);
      return url;
    }
  }

  // Non-blob remote URL (rare)
  if (asset.url && !asset.url.startsWith('blob:')) {
    urlCache.set(asset.id, asset.url);
    return asset.url;
  }

  throw new Error(`No media blob for asset ${asset.id}`);
}

export function forgetAssetUrl(assetId: string) {
  const url = urlCache.get(assetId);
  if (url?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
  urlCache.delete(assetId);
}
